import { z } from "zod";

import { CIPHER_SECURITY_LEVELS, CIPHER_STATUSES } from "@cryptolect/core";

// Re-export `CipherMapping` so SDK consumers building mappings programmatically
// can still pull the strict shape from one place — the input schema below is
// deliberately looser to match the wire contract.
export type { CipherMapping } from "@cryptolect/core";

// Stable error codes — kept in sync with `apps/api/src/lib/validation.ts`
// (`ERROR_CODES`) and `docs/prd/AGENT_INTERFACE_SPEC.md` §"Stable Error Codes".
// `apps/web` and `packages/sdk` import these so they can branch on the wire
// code without re-deriving the union.
export const ERROR_CODES = [
  "invalid_input",
  "invalid_token",
  "quota_exceeded",
  "cipher_not_granted",
  "cipher_not_found",
  "decode_failed",
  "upstream_error",
  "rate_limited",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const errorCodeSchema = z.enum(ERROR_CODES);

// Two failure shapes the API speaks today:
//   1. Structured: `{ error: { code, message, issues? } }` — every route helper
//      built on `apps/api/src/lib/responses.ts:err` and `validation.ts:httpError`.
//   2. Legacy: `{ error: "unauthorized" }` — middleware that pre-dates the
//      structured envelope (`requireSession` 401, missing-cred fallback in
//      `requireAuth`). Surfaced by `/v1/me`, the `/v1/ciphers` mount, and
//      `/v1/api-keys` when no Authorization header is present.
// The client maps both into a single `CryptolectApiError` carrying an
// `invalid_token` code for the legacy case so callers can branch uniformly.
export const structuredErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    issues: z.array(z.unknown()).optional(),
  }),
});

export const legacyErrorSchema = z.object({
  error: z.string(),
});

export const errorBodySchema = z.union([structuredErrorSchema, legacyErrorSchema]);

export function successEnvelope<T extends z.ZodType>(data: T) {
  return z.object({ data });
}

export const securityLevelSchema = z.enum(CIPHER_SECURITY_LEVELS);
export const statusSchema = z.enum(CIPHER_STATUSES);

// `CipherMapping` from `@cryptolect/core` is structurally validated at
// encrypt/decrypt time. At the client boundary we accept the loose wire shape
// the server accepts (`forward` + `reverse` records, optional code/version)
// so callers can pass mappings authored either by `@cryptolect/core` or by
// 3MCRYPT3R fixtures verbatim. The strict `CipherMapping` is assignable to
// this looser shape, so consumers can still pass core-typed mappings.
export const cipherMappingSchema = z
  .object({
    forward: z.record(z.string(), z.unknown()),
    reverse: z.record(z.string(), z.unknown()),
    cipher_code: z.string().optional(),
    version: z.string().optional(),
  })
  .loose();
export type CipherMappingInput = z.infer<typeof cipherMappingSchema>;

export const publicCipherSchema = z.object({
  id: z.string(),
  cipher_code: z.string(),
  owner_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  security_level: securityLevelSchema,
  status: statusSchema,
  mapping_version: z.number().int(),
  character_count: z.number().int(),
  is_public: z.boolean(),
  has_passcode: z.boolean(),
  created_at: z.number(),
  updated_at: z.number(),
});
export type PublicCipher = z.infer<typeof publicCipherSchema>;

export const cipherWithMappingSchema = publicCipherSchema.extend({
  mapping: z.unknown(),
});
export type CipherWithMapping = z.infer<typeof cipherWithMappingSchema>;

export const publicApiKeySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  key_prefix: z.string(),
  scoped_cipher_id: z.string().nullable(),
  created_at: z.number(),
  last_used_at: z.number().nullable(),
  expires_at: z.number().nullable(),
});
export type PublicApiKey = z.infer<typeof publicApiKeySchema>;

export const createdApiKeySchema = publicApiKeySchema.extend({
  plaintext: z.string(),
});
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;

// Request schemas — what callers pass into client methods. Field names match
// the wire JSON exactly so a request body is just `JSON.stringify(input)`.
export const createCipherInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).optional(),
  mapping: cipherMappingSchema,
  passcode: z.string().min(1).max(256).optional(),
  security_level: securityLevelSchema,
  status: statusSchema.optional(),
  is_public: z.boolean().optional(),
});
export type CreateCipherInput = z.infer<typeof createCipherInputSchema>;

export const updateCipherInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    security_level: securityLevelSchema.optional(),
  })
  .refine((value) => value.name !== undefined || value.security_level !== undefined, {
    message: "At least one of `name` or `security_level` must be provided",
  });
export type UpdateCipherInput = z.infer<typeof updateCipherInputSchema>;

export const listCiphersInputSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type ListCiphersInput = z.infer<typeof listCiphersInputSchema>;

export const encodeInputSchema = z.object({
  cipher_code: z.string().trim().min(1).max(64),
  plaintext: z.string(),
});
export type EncodeInput = z.infer<typeof encodeInputSchema>;

export const decodeInputSchema = z.object({
  cipher_code: z.string().trim().min(1).max(64),
  emojitext: z.string(),
});
export type DecodeInput = z.infer<typeof decodeInputSchema>;

export const createApiKeyInputSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  scoped_cipher_code: z.string().trim().min(1).max(64).optional(),
  expires_at: z.number().int().positive().optional(),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeyInputSchema>;

// Response payloads — what `data` looks like inside `{ data: ... }` envelopes.
export const listCiphersDataSchema = z.object({
  ciphers: z.array(publicCipherSchema),
  next_cursor: z.string().nullable(),
});
export type ListCiphersData = z.infer<typeof listCiphersDataSchema>;

export const cipherDataSchema = z.object({
  cipher: z.union([publicCipherSchema, cipherWithMappingSchema]),
});

export const deletedCipherDataSchema = z.object({
  deleted: z.literal(true),
  cipher_code: z.string(),
});
export type DeletedCipherData = z.infer<typeof deletedCipherDataSchema>;

export const encodeDataSchema = z.object({
  cipher_code: z.string(),
  encoded: z.string(),
  length: z.number().int(),
});
export type EncodeData = z.infer<typeof encodeDataSchema>;

export const decodeDataSchema = z.object({
  cipher_code: z.string(),
  decoded: z.string(),
  character_count: z.number().int(),
});
export type DecodeData = z.infer<typeof decodeDataSchema>;

export const createApiKeyDataSchema = z.object({
  api_key: createdApiKeySchema,
});

export const listApiKeysDataSchema = z.object({
  api_keys: z.array(publicApiKeySchema),
});
export type ListApiKeysData = z.infer<typeof listApiKeysDataSchema>;

export const revokedApiKeyDataSchema = z.object({
  revoked: z.literal(true),
  key_prefix: z.string(),
});
export type RevokedApiKeyData = z.infer<typeof revokedApiKeyDataSchema>;

// — Verification (Phase 04) —
//
// Two shapes:
//   * REST DTO (snake_case) — what `POST /v1/ciphers/:code/verifications`
//     and `GET /v1/ciphers/:code/verifications` return; mirrors
//     `apps/api/src/routes/verifications.ts:toDto`.
//   * Stream summary (camelCase) — what the DO emits over WebSocket frames;
//     mirrors `apps/api/src/durable-objects/verification-workflow.ts:WireEvent`.
// The two cases co-exist because the REST routes serialize through the
// usage-spec snake_case envelope, but the WS frames bypass the route layer.
export const verificationStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "expired",
]);
export type VerificationStatus = z.infer<typeof verificationStatusSchema>;

export const verificationSchema = z.object({
  id: z.string(),
  cipher_id: z.string(),
  requester_id: z.string(),
  reason: z.string().nullable(),
  status: verificationStatusSchema,
  requested_at: z.number(),
  decided_at: z.number().nullable(),
  decided_by: z.string().nullable(),
  decision_note: z.string().nullable(),
  expires_at: z.number(),
});
export type Verification = z.infer<typeof verificationSchema>;

// Stream summary — what the DO broadcasts on `created` frames and inside
// the `snapshot` array. Smaller surface than the REST DTO because the DO
// only tracks the fields it needs for fan-out and alarm scheduling.
export const activeVerificationSchema = z.object({
  id: z.string(),
  requesterId: z.string(),
  status: verificationStatusSchema,
  expiresAt: z.number(),
});
export type ActiveVerification = z.infer<typeof activeVerificationSchema>;

export const createVerificationInputSchema = z
  .object({
    reason: z.string().trim().max(2000).optional(),
    expires_at: z.number().int().positive().optional(),
  })
  .strict();
export type CreateVerificationInput = z.infer<
  typeof createVerificationInputSchema
>;

export const decideVerificationInputSchema = z
  .object({
    decision: z.enum(["approve", "reject"]),
    note: z.string().trim().max(2000).optional(),
  })
  .strict();
export type DecideVerificationInput = z.infer<
  typeof decideVerificationInputSchema
>;

export const listVerificationsInputSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type ListVerificationsInput = z.infer<
  typeof listVerificationsInputSchema
>;

export const verificationDataSchema = z.object({
  verification: verificationSchema,
});

export const listVerificationsDataSchema = z.object({
  verifications: z.array(verificationSchema),
  next_cursor: z.string().nullable(),
});
export type ListVerificationsData = z.infer<
  typeof listVerificationsDataSchema
>;

// WebSocket frame schema — every payload streamed from
// `/v1/ciphers/:code/verifications/live`. `discriminatedUnion` fails closed
// on an unknown `type`, so a malformed frame surfaces via the stream's
// `onError` callback rather than silently being delivered as a typed event.
export const verificationStreamEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("snapshot"),
    requests: z.array(activeVerificationSchema),
  }),
  z.object({
    type: z.literal("created"),
    request: activeVerificationSchema,
  }),
  z.object({
    type: z.literal("decided"),
    id: z.string(),
    status: z.enum(["approved", "rejected"]),
    decidedAt: z.number(),
    decidedBy: z.string(),
  }),
  z.object({
    type: z.literal("expired"),
    ids: z.array(z.string()),
  }),
]);
export type VerificationStreamEvent = z.infer<
  typeof verificationStreamEventSchema
>;

// `/v1/me` is the one route that does not wrap in `{ data }` — it returns
// `{ user, session }` directly because Better Auth's session shape is opaque
// to us. Loose object so future Better Auth versions can add fields without
// forcing a client release.
export const meResponseSchema = z
  .object({
    user: z.object({ id: z.string() }).loose(),
    session: z.object({ id: z.string() }).loose(),
  })
  .loose();
export type MeResponse = z.infer<typeof meResponseSchema>;
